(() => {
  'use strict';

  const roots = document.querySelectorAll(
    '[data-f2w-sport-group-page]'
  );

  if (!roots.length) {
    return;
  }

  const shuffle = (items) => {
    const shuffled = [...items];

    for (let index = shuffled.length - 1; index > 0; index -= 1) {
      const randomIndex = Math.floor(Math.random() * (index + 1));

      [shuffled[index], shuffled[randomIndex]] = [
        shuffled[randomIndex],
        shuffled[index]
      ];
    }

    return shuffled;
  };

  const idKeys = (value) => {
    const raw = String(value || '').trim();

    if (!raw) {
      return [];
    }

    const short = raw.split('/').pop() || raw;

    return [...new Set([raw, short])];
  };

  const idSetHas = (set, value) => {
    return idKeys(value).some((key) => set.has(key));
  };

  const nativeUrl = (value) => {
    if (!value) {
      return '';
    }

    try {
      const url = new URL(String(value), window.location.origin);

      if (url.protocol !== 'http:' && url.protocol !== 'https:') {
        return '';
      }

      return url.href;
    } catch (error) {
      return '';
    }
  };

  const fieldValue = (field) => {
    return String(field?.value || '').trim();
  };

  const nodeLabel = (node) => {
    return String(
      node?.name?.value ||
      node?.displayName ||
      node?.handle ||
      ''
    ).trim();
  };

  const imageUrl = (field) => {
    if (!field) {
      return '';
    }

    if (field.reference?.image?.url) {
      return field.reference.image.url;
    }

    const imageNode = (field.references?.nodes || []).find((node) => {
      return node?.image?.url;
    });

    return imageNode?.image?.url || '';
  };

  const parseImageMap = (element) => {
    if (!element) {
      return new Map();
    }

    try {
      const entries = JSON.parse(element.textContent || '[]');

      return new Map(
        entries
          .filter((entry) => entry?.handle)
          .map((entry) => [String(entry.handle), entry.image || ''])
      );
    } catch (error) {
      return new Map();
    }
  };

  const countryFlagElement = (code, label) => {
    const flagCode = String(code || '')
      .trim()
      .toLowerCase();

    const isNation =
      flagCode === 'gb-sct' ||
      flagCode === 'gb-eng' ||
      flagCode === 'gb-wls';

    if (
      !flagCode ||
      (flagCode.length !== 2 && !isNation)
    ) {
      return null;
    }

    const wrapper = document.createElement('span');
    wrapper.className = 'f2w-country-flag';

    const image = document.createElement('img');
    image.src =
      `https://flagcdn.com/w40/${flagCode}.png`;
    image.srcset =
      `https://flagcdn.com/w80/${flagCode}.png 2x`;
    image.alt = String(label || '');
    image.width = 20;
    image.height = 15;
    image.loading = 'lazy';
    image.decoding = 'async';

    wrapper.appendChild(image);
    wrapper.appendChild(
      document.createTextNode('\u00a0')
    );

    return wrapper;
  };

  const createElement = (tag, className, text) => {
    const element = document.createElement(tag);

    if (className) {
      element.className = className;
    }

    if (text !== undefined && text !== null) {
      element.textContent = text;
    }

    return element;
  };

  const sportsQuery = `
    query F2WSportGroupSports($cursor: String, $language: LanguageCode)
      @inContext(language: $language) {
      metaobjects(
        type: "sport"
        first: 250
        after: $cursor
      ) {
        pageInfo {
          hasNextPage
          endCursor
        }
        nodes {
          id
          sport_group: field(key: "sport_group") {
            value
          }
        }
      }
    }
  `;

  const fightersQuery = `
    query F2WSportGroupFighters($cursor: String, $language: LanguageCode)
      @inContext(language: $language) {
      metaobjects(
        type: "fighter_profile"
        first: 250
        after: $cursor
      ) {
        pageInfo {
          hasNextPage
          endCursor
        }
        nodes {
          id
          handle
          onlineStoreUrl

          name: field(key: "name") {
            value
          }

          resolved_image_url: field(key: "resolved_image_url") { value }
          is_legend: field(key: "is_legend") { value }

          nickname: field(key: "nickname") {
            value
          }

          country: field(key: "country") {
            reference {
              ... on Metaobject {
                name: field(key: "name") {
                  value
                }

                code: field(key: "code") {
                  value
                }
              }
            }
          }

          sports: field(key: "sports") {
            value
            references(first: 50) {
              nodes {
                ... on Metaobject {
                  id
                  name: field(key: "name") {
                    value
                  }
                }
              }
            }
          }

          cover_image: field(key: "cover_image") {
            reference {
              ... on MediaImage {
                image {
                  url(transform: { maxWidth: 900 })
                }
              }
            }
          }

          photo: field(key: "photo") {
            reference {
              ... on MediaImage {
                image {
                  url(transform: { maxWidth: 900 })
                }
              }
            }
          }

          images: field(key: "images") {
            references(first: 10) {
              nodes {
                ... on MediaImage {
                  image {
                    url(transform: { maxWidth: 900 })
                  }
                }
              }
            }
          }
        }
      }
    }
  `;

  const request = async (root, query, cursor, language) => {
    const token = root.dataset.storefrontToken || '';

    if (!token) {
      throw new Error('Missing Storefront token');
    }

    const shop = root.dataset.shopDomain || window.location.hostname;
    const apiVersion = root.dataset.apiVersion || '2024-10';
    const endpoint = `https://${shop}/api/${apiVersion}/graphql.json`;

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token
      },
      body: JSON.stringify({
        query,
        variables: {
          cursor: cursor || null,
          language: language || null
        }
      })
    });

    if (!response.ok) {
      throw new Error('Storefront request failed');
    }

    const json = await response.json();

    if (Array.isArray(json.errors) && json.errors.length) {
      throw new Error('Storefront query failed');
    }

    return json.data?.metaobjects || null;
  };

  const loadAll = async (root, query, language) => {
    const nodes = [];
    const seenCursors = new Set();
    let cursor = null;

    do {
      if (cursor && seenCursors.has(cursor)) {
        throw new Error('Repeated Storefront cursor');
      }

      if (cursor) {
        seenCursors.add(cursor);
      }

      const page = await request(root, query, cursor, language);

      if (!page || !Array.isArray(page.nodes) || typeof page.pageInfo?.hasNextPage !== 'boolean') {
        throw new Error('Missing Storefront data');
      }

      nodes.push(...(page.nodes || []));

      const hasNextPage = Boolean(page.pageInfo?.hasNextPage);
      const nextCursor = page.pageInfo?.endCursor || null;

      if (hasNextPage && !nextCursor) {
        throw new Error('Missing Storefront cursor');
      }

      cursor = hasNextPage ? nextCursor : null;
    } while (cursor);

    return nodes;
  };

  const createImageMapState = (root) => {
    const element = root.querySelector(
      '[data-f2w-sport-group-fighter-image-map]'
    );

    const images = parseImageMap(element);

    const firstPage = Math.max(
      1,
      parseInt(element?.dataset.imagePage, 10) || 1
    );

    const totalPages = Math.max(
      firstPage,
      parseInt(element?.dataset.imagePages, 10) || firstPage
    );

    const loadedPages = new Set([firstPage]);
    const pageRequests = new Map();
    let nextPage = firstPage + 1;

    const requestPage = async (pageNumber) => {
      if (
        pageNumber < 1 ||
        pageNumber > totalPages ||
        loadedPages.has(pageNumber)
      ) {
        return;
      }

      if (pageRequests.has(pageNumber)) {
        return pageRequests.get(pageNumber);
      }

      const pageRequest = (async () => {
        const url = new URL(window.location.href);
        url.searchParams.set('page', String(pageNumber));

        const response = await fetch(url.toString(), {
          credentials: 'same-origin',
          headers: {
            Accept: 'text/html'
          }
        });

        if (!response.ok) {
          throw new Error('Fighter image page request failed');
        }

        const html = await response.text();

        const parsed = new DOMParser().parseFromString(
          html,
          'text/html'
        );

        const pageElement = parsed.querySelector(
          '[data-f2w-sport-group-fighter-image-map]'
        );

        if (!pageElement) {
          throw new Error('Fighter image map not found');
        }

        parseImageMap(pageElement).forEach((image, handle) => {
          images.set(handle, image);
        });

        loadedPages.add(pageNumber);
      })();

      pageRequests.set(pageNumber, pageRequest);

      try {
        await pageRequest;
      } finally {
        pageRequests.delete(pageNumber);
      }
    };

    const ensureImageEntries = async (fighters) => {
      const hasMissingImages = () => {
        return fighters.some((fighter) => {
          return (
            fighter.handle &&
            !fighter.image &&
            !images.has(fighter.handle)
          );
        });
      };

      while (hasMissingImages() && nextPage <= totalPages) {
        const pageToLoad = nextPage;
        nextPage += 1;

        try {
          await requestPage(pageToLoad);
        } catch (error) {
          break;
        }
      }

      fighters.forEach((fighter) => {
        if (
          !fighter.image &&
          fighter.handle &&
          images.has(fighter.handle)
        ) {
          fighter.image = images.get(fighter.handle) || '';
        }
      });
    };

    return {
      ensureImageEntries
    };
  };

  const mapFighter = (node, imageMap) => {
    const handle = String(node.handle || '').trim();

    return {
      id: node.id || '',
      handle,
      url: nativeUrl(node.onlineStoreUrl),
      name: fieldValue(node.name),
      nickname: fieldValue(node.nickname),
      country: nodeLabel(node.country?.reference),
      countryCode: String(
        node.country?.reference?.code?.value ||
        ''
      ).trim(),
      sports: (node.sports?.references?.nodes || [])
        .map((sport) => ({
          id: sport.id || '',
          name: nodeLabel(sport)
        }))
        .filter((sport) => sport.id || sport.name),
      image:
        fieldValue(node.resolved_image_url) ||
        imageUrl(node.cover_image) ||
        imageUrl(node.photo) ||
        imageUrl(node.images) ||
        imageMap.get(handle) ||
        ''
    };
  };

  const selectFeatured = (fighters, sportIds, limit) => {
    const selected = [];
    const selectedIds = new Set();

    shuffle([...sportIds]).forEach((sportId) => {
      if (selected.length >= limit) {
        return;
      }

      const candidates = shuffle(
        fighters.filter((fighter) => {
          return (
            !selectedIds.has(fighter.id) &&
            fighter.sports.some((sport) => {
              return idSetHas(
                new Set(idKeys(sportId)),
                sport.id
              );
            })
          );
        })
      );

      const candidate = candidates[0];

      if (candidate) {
        selected.push(candidate);
        selectedIds.add(candidate.id);
      }
    });

    if (selected.length < limit) {
      shuffle(fighters)
        .filter((fighter) => !selectedIds.has(fighter.id))
        .slice(0, limit - selected.length)
        .forEach((fighter) => selected.push(fighter));
    }

    return shuffle(selected).slice(0, limit);
  };

  const createFighterCard = (fighter, viewLabel) => {
    const article = createElement(
      'article',
      'f2w-event-card'
    );

    article.dataset.f2wSportGroupFighterCard = 'true';

    const cardContent = fighter.url
      ? createElement('a', 'f2w-event-card__link')
      : createElement('div', 'f2w-event-card__link');

    if (fighter.url) {
      cardContent.href = fighter.url;
    }

    const imageWrapper = createElement(
      'div',
      'f2w-event-card__image'
    );

    if (fighter.image) {
      const image = document.createElement('img');

      image.src = fighter.image;
      image.alt = fighter.name;
      image.width = 900;
      image.height = 900;
      image.loading = 'lazy';

      imageWrapper.appendChild(image);
    } else {
      const placeholder = createElement(
        'div',
        'f2w-event-card__image--placeholder'
      );

      placeholder.appendChild(
        createElement(
          'span',
          '',
          fighter.name.slice(0, 1)
        )
      );

      imageWrapper.appendChild(placeholder);
    }

    const body = createElement(
      'div',
      'f2w-event-card__body'
    );

    const content = createElement(
      'div',
      'f2w-event-card__content'
    );

    if (fighter.nickname) {
      content.appendChild(
        createElement(
          'p',
          'f2w-event-card__organization',
          fighter.nickname
        )
      );
    }

    content.appendChild(
      createElement(
        'h3',
        'f2w-event-card__title',
        fighter.name
      )
    );

    if (fighter.country) {
      const countryElement = createElement(
        'p',
        'f2w-event-card__location',
        fighter.country
      );

      const countryFlag = countryFlagElement(
        fighter.countryCode,
        fighter.country
      );

      if (countryFlag) {
        countryElement.insertBefore(
          countryFlag,
          countryElement.firstChild
        );
      }

      content.appendChild(countryElement);
    }

    if (fighter.sports.length) {
      const sports = createElement(
        'div',
        'f2w-event-card__sports'
      );

      fighter.sports.forEach((sport) => {
        if (sport.name) {
          sports.appendChild(
            createElement(
              'span',
              'f2w-event-card__sport',
              sport.name
            )
          );
        }
      });

      if (sports.childElementCount) {
        content.appendChild(sports);
      }
    }

    if (fighter.url && viewLabel) {
      content.appendChild(
        createElement(
          'span',
          'f2w-event-card__view',
          viewLabel
        )
      );
    }

    body.appendChild(content);
    cardContent.appendChild(imageWrapper);
    cardContent.appendChild(body);
    article.appendChild(cardContent);

    return article;
  };

  const readWorldCountIndex = (root, kind) => {
    const element = root.querySelector(`[data-f2w-world-${kind}-index]`);
    if (!element) throw new Error('Missing world count index');
    const packet = JSON.parse(element.textContent || 'null');
    const manifest = packet?.manifest;
    if (manifest?.v !== 1 || !Number.isInteger(manifest.count) || manifest.count < 0) throw new Error('Invalid world count manifest');
    let parts = [manifest];
    if (manifest.sharded === true) {
      if (!Number.isInteger(manifest.parts) || manifest.parts < 1 || !Array.isArray(manifest.keys) || manifest.keys.length !== manifest.parts || new Set(manifest.keys).size !== manifest.parts || !manifest.generatedAt || !Array.isArray(packet.parts) || packet.parts.length !== manifest.parts) throw new Error('Incomplete world count shards');
      parts = packet.parts;
      if (parts.some((part, index) => part?.v !== manifest.v || part.part !== index + 1 || part.parts !== manifest.parts || part.generatedAt !== manifest.generatedAt)) throw new Error('Mismatched world count shards');
    }
    const rows = parts.flatMap(part => {
      if (!Array.isArray(part.rows)) throw new Error('Missing world count rows');
      return part.rows;
    });
    const sportsColumn = kind === 'fighters' ? 2 : 3;
    if (rows.length !== manifest.count || rows.some(row => !Array.isArray(row) || typeof row[0] !== 'string' || !row[0] || !Array.isArray(row[sportsColumn]) || row[sportsColumn].some(id => typeof id !== 'string' || !id))) throw new Error('Incomplete world count relations');
    const ids = rows.map(row => idKeys(row[0]).pop());
    if (new Set(ids).size !== manifest.count) throw new Error('Duplicate world count index rows');
    return { rows, sportsColumn };
  };

  const publishIndexedWorldPeopleCounts = (root, groupSportKeys) => {
    if (!root.querySelector('[data-f2w-world-overview]')) return;
    const formatter = new Intl.NumberFormat(root.dataset.language || 'nl');
    ['fighters', 'legends'].forEach(kind => {
      try {
        const { rows, sportsColumn } = readWorldCountIndex(root, kind);
        const matching = new Set(rows.filter(row => row[sportsColumn].some(id => idSetHas(groupSportKeys, id))).map(row => idKeys(row[0]).pop()));
        const element = root.querySelector(`[data-f2w-world-count="${kind}"]`);
        if (element) element.textContent = formatter.format(matching.size);
      } catch (error) {
        // Leave unknown until the existing complete profile load can reconcile it.
      }
    });
  };

  const initRoot = async (root) => {
    if (
      root.dataset.f2wSportGroupFightersInitialized === 'true'
    ) {
      return;
    }

    root.dataset.f2wSportGroupFightersInitialized = 'true';

    const section = root.querySelector(
      '[data-f2w-sport-group-fighters-section]'
    );

    const grid = root.querySelector(
      '[data-f2w-sport-group-fighters]'
    );

    if (
      ((!section || !grid) && !root.querySelector('[data-f2w-world-overview]')) ||
      !root.dataset.storefrontToken
    ) {
      return;
    }

    const groupKey = String(
      root.dataset.groupKey || ''
    ).trim();

    const language = String(
      root.dataset.language ||
      document.documentElement?.lang ||
      ''
    )
      .split('-')[0]
      .trim()
      .toUpperCase() || null;

    const imageMapState = createImageMapState(root);

    try {
      const sportsPromise = loadAll(root, sportsQuery, String(root.dataset.sourceLanguage || 'NL').split('-')[0].toUpperCase());
      const fighterPromise = loadAll(root, fightersQuery, language).then(
        nodes => ({ nodes }),
        error => ({ error })
      );
      const sports = await sportsPromise;

      const groupSportIds = sports
        .filter((sport) => {
          return (
            String(
              sport.sport_group?.value || ''
            ).trim() === groupKey
          );
        })
        .map((sport) => {
          return String(sport.id || '').trim();
        })
        .filter(Boolean);

      const groupSportKeys = new Set(
        groupSportIds.flatMap(idKeys)
      );
      publishIndexedWorldPeopleCounts(root, groupSportKeys);
      const fighterResult = await fighterPromise;
      if (fighterResult.error) throw fighterResult.error;
      const fighterNodes = fighterResult.nodes;


      if (root.querySelector('[data-f2w-world-overview]')) {
        const matching = new Map();
        fighterNodes.forEach(node => {
          const raw = fieldValue(node.sports);
          const ids = raw ? JSON.parse(raw) : (node.sports?.references?.nodes || []).map(sport => sport.id);
          if (!Array.isArray(ids)) throw new Error('Incomplete fighter relations');
          if (node.id && ids.some(id => idSetHas(groupSportKeys,id))) matching.set(node.id,node);
        });
        const formatter = new Intl.NumberFormat(root.dataset.language || 'nl');
        const total = root.querySelector('[data-f2w-world-count="fighters"]');
        const legends = root.querySelector('[data-f2w-world-count="legends"]');
        if (total) total.textContent = formatter.format(matching.size);
        if (legends) legends.textContent = formatter.format([...matching.values()].filter(node => ['true','1'].includes(fieldValue(node.is_legend).toLowerCase())).length);
      }
      if (!section || !grid) return;

      if (!groupSportIds.length) {
        section.hidden = true;
        return;
      }

      const imageMapElement = root.querySelector(
        '[data-f2w-sport-group-fighter-image-map]'
      );

      const imageMap = parseImageMap(imageMapElement);

      const fighters = fighterNodes
        .map((node) => {
          return mapFighter(node, imageMap);
        })
        .filter((fighter) => {
          return (
            fighter.name &&
            fighter.sports.some((sport) => {
              return idSetHas(groupSportKeys, sport.id);
            })
          );
        });

      const selected = selectFeatured(
        fighters,
        groupSportIds,
        3
      );

      await imageMapState.ensureImageEntries(selected);

      grid.replaceChildren();

      selected.forEach((fighter) => {
        grid.appendChild(
          createFighterCard(
            fighter,
            String(
              root.dataset.labelFighterView || ''
            ).trim()
          )
        );
      });

      section.hidden = selected.length === 0;
    } catch (error) {
      if (section) section.hidden = true;
    }
  };

  roots.forEach((root) => {
    initRoot(root);
  });
})();



